// consent/callbacks.ts

import { getConsentStatus } from './consentManager';

type ConsentType = 'essential' | 'functional' | 'marketing';

interface ConsentCallbacks {
	onGrant: (() => void)[];
	onRevoke: (() => void)[];
}

const consentCallbacks: { [key in ConsentType]: ConsentCallbacks } = {
	essential: { onGrant: [], onRevoke: [] },
	functional: { onGrant: [], onRevoke: [] },
	marketing: { onGrant: [], onRevoke: [] },
};

/**
 * Registers a callback to be executed when consent is granted or revoked.
 * Executes the callback immediately if the consent status already matches the requested type.
 *
 * @param {ConsentType} consentType - The type of consent to register for.
 * @param {() => void} callback - The callback function to register.
 * @param {'grant' | 'revoke'} type - The type of event (grant or revoke) to register the callback for.
 */
export function registerCallback(
	consentType: ConsentType,
	callback: () => void,
	type: 'grant' | 'revoke',
): void {
	const consentGranted = getConsentStatus(consentType);

	if (type === 'grant' && consentGranted) {
		// Execute the callback immediately if consent is already granted
		callback();
	} else if (type === 'revoke' && !consentGranted) {
		// Execute the callback immediately if consent is already revoked
		callback();
	} else {
		// Otherwise, store the callback for future use
		if (type === 'grant') {
			consentCallbacks[consentType].onGrant.push(callback);
		} else {
			consentCallbacks[consentType].onRevoke.push(callback);
		}
	}
}

/**
 * Executes and clears all callbacks for a specific consent type and event type.
 *
 * @param {ConsentType} consentType - The type of consent to execute callbacks for.
 * @param {'grant' | 'revoke'} type - The type of event (grant or revoke) to execute callbacks for.
 * @returns {(() => void)[]} - The list of callbacks that were executed.
 */
export function executeCallbacks(
	consentType: ConsentType,
	type: 'grant' | 'revoke',
): (() => void)[] {
	const callbacks =
		type === 'grant'
			? consentCallbacks[consentType].onGrant
			: consentCallbacks[consentType].onRevoke;
	const callbacksToExecute = [...callbacks];
	if (type === 'grant') {
		consentCallbacks[consentType].onGrant = [];
	} else {
		consentCallbacks[consentType].onRevoke = [];
	}
	return callbacksToExecute;
}
